import { useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EditIcon,
  EllipsisMenu,
  Table,
  Bin,
  Prompt,
  SnackbarStatus,
  useSnackbar,
  Typography,
  LinkButton,
  AddPriceBandIcon,
  PriceBandIcon,
  TextIcon,
  ViewIcon,
  DuplicateIcon,
  useTray,
} from '@weareopenr/component-library';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import SkeletonContainer from 'src/components/SkeletonContainer';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import { Routes } from 'src/routes';
import { serializeCategoriesParam, UNCATEGORISED_ID } from 'src/utils/venueCategories/mappedVenueCategories';
import { useUpdateCategoryVenues } from 'src/hooks/useUpdateCategoryVenues';
import { useUpdateVenueCategory } from 'src/hooks/useUpdateVenueCategory';
import { IGetVenuesResponseData } from 'src/api/getVenues/types';
import { ITableCategory } from './types';
import { mapCategoriesToTableData } from './utilities/mapCategoriesToTableData';
import styles from './VenueCategoriesTab.module.scss';
import { InlineNameForm } from 'src/components/InlineNameForm/InlineNameForm';
import VenueAllocationModal from '../VenueAllocationModal';
import { pluralize } from 'src/helpers/pluralize/pluralize';
import { deleteVenueCategory } from 'src/api/deleteVenueCategory/deleteVenueCategory';
import { createVenueCategory } from 'src/api/createVenueCategory/createVenueCategory';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { useQueryClient } from '@tanstack/react-query';
import { MoveToCategoryPrompt } from '../MoveToCategoryPrompt';
import { DuplicateVenueCategoryTrayContent } from '../DuplicateVenueCategoryTray';
import { getNextOpenId } from 'src/helpers/getNextOpenId';

const columnHelper = createColumnHelper<ITableCategory>();

const MIN_ROOT_CATEGORIES = 1;

const VenueCategoriesTab = ({
  venues,
  isSyncInProgress = false,
}: {
  venues: IGetVenuesResponseData[];
  isSyncInProgress?: boolean;
}) => {
  const { nestedCategories, orderedFlatCategories, isLoading: isCategoriesLoading, data } = useVenueCategories();
  const { mutate: updateCategoryVenues, isLoading: isUpdating } = useUpdateCategoryVenues();
  const {
    mutate: updateVenueCategory,
    mutateAsync: updateVenueCategoryAsync,
    isLoading: isMoveLoading,
  } = useUpdateVenueCategory();

  const [categoryToEdit, setCategoryToEdit] = useState<ITableCategory | null>(null);
  const [categoryToDelete, setCategoryToDelete] = useState<ITableCategory | null>(null);
  const [categoryToMove, setCategoryToMove] = useState<ITableCategory | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [selectedCategoryToEditName, setSelectedCategoryToEditName] = useState<string | null>(null);
  const [isCreateCategoryOpen, setIsCreateCategoryOpen] = useState<boolean>(false);

  const categories = data?.categories ?? [];

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { openTray, closeTray } = useTray();
  const [parentCategoryId, setParentCategoryId] = useState<string | null>(null);

  const handleEditClick = (category: ITableCategory) => setCategoryToEdit(category);

  const handleViewVenuesClick = (categoryId: string | null) => {
    const id = categoryId ?? UNCATEGORISED_ID;
    navigate({
      pathname: Routes.VENUES,
      search: `?${serializeCategoriesParam([id])}`,
      hash: '#venues',
    });
  };

  const handleCreateSubCategoryClick = (parentCategoryId: string) => {
    setParentCategoryId(parentCategoryId);
    setIsCreateCategoryOpen(true);
  };

  const reorderCategory = (categoryId: string, newDisplayOrder: number) =>
    updateVenueCategory({ categoryId, newDisplayOrder });

  const handleUpdateCategoryVenuesSubmit = (data: { categoryId: string; venueIds: string[] }) => {
    updateCategoryVenues(data, {
      onSuccess: () => {
        setCategoryToEdit(null);
      },
    });
  };

  const openDuplicateTray = (category: ITableCategory) => {
    openTray(
      <DuplicateVenueCategoryTrayContent
        openId={getNextOpenId()}
        categoryId={category.category_id}
        sourceName={category.name}
        onClose={() => closeTray()}
      />,
      { title: 'Duplicate Category' },
    );
  };

  const handleDeleteClick = (category: ITableCategory) => setCategoryToDelete(category);
  const handleMoveToCategoryClick = (category: ITableCategory) => setCategoryToMove(category);

  const handleMoveToCategory = (newParentId: string | null) => {
    if (!categoryToMove?.category_id) return;

    updateVenueCategory(
      { categoryId: categoryToMove.category_id, newParentId },
      {
        onSuccess: () => {
          setCategoryToMove(null);
        },
      },
    );
  };

  const handleInlineCategoryFormSubmit = async (name: string, original: ITableCategory) => {
    if (original.isCreateRow) {
      const [newCategoryId] = await createVenueCategory([{ name, parent_id: parentCategoryId }]);

      try {
        await updateVenueCategoryAsync({ categoryId: newCategoryId, newDisplayOrder: 1 });
      } catch {
        // The category already exists, so close the form instead of rethrowing: a retry would create a duplicate.
        // The hook has shown the error; refresh so the new category appears in its default position.
        await queryClient.invalidateQueries({ queryKey: [getBrandId(), QueryKeys.venueCategories] });
      }
    } else {
      await updateVenueCategoryAsync({ categoryId: original.category_id, newName: name });
    }
  };

  const handleDelete = async () => {
    if (!categoryToDelete?.category_id) return;

    setIsDeleteLoading(true);
    try {
      await deleteVenueCategory(categoryToDelete.category_id);
      await queryClient.invalidateQueries({ queryKey: [getBrandId(), QueryKeys.venueCategories] });
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `Category "${categoryToDelete.name}" has been deleted successfully`,
      });
      setCategoryToDelete(null);
    } catch (error) {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: `Failed to delete category "${categoryToDelete.name}": ${error instanceof Error ? error.message : 'Unknown error'}`,
      });
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const totalVenues = venues.length;

  const tableData: ITableCategory[] = useMemo(() => {
    return mapCategoriesToTableData(nestedCategories, isCreateCategoryOpen, parentCategoryId);
  }, [nestedCategories, isCreateCategoryOpen]);

  const areCrudActionsDisabled = isCreateCategoryOpen || !!selectedCategoryToEditName;

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ row, getValue }) => {
          const original = row.original;
          if (
            original.isCreateRow ||
            (row.original.category_id === selectedCategoryToEditName && !original.isUncategorised)
          ) {
            return (
              <InlineNameForm
                onSubmit={(name) => handleInlineCategoryFormSubmit(name, original)}
                onClose={() => {
                  setIsCreateCategoryOpen(false);
                  setParentCategoryId(null);
                  setSelectedCategoryToEditName(null);
                }}
                existingNames={categories.filter((cat) => cat.name !== original.name).map((cat) => cat.name)}
                defaultValue={original.isCreateRow ? '' : original.name}
              />
            );
          }
          return getValue();
        },
        footer: () => (
          <LinkButton onClick={() => setIsCreateCategoryOpen((prev) => !prev)} disabled={areCrudActionsDisabled}>
            + Create Category
          </LinkButton>
        ),
      }),
      columnHelper.accessor('subCategoriesCount', {
        header: 'Sub-categories',
        size: 150,
        cell: (info) => {
          if (info.row.original.isCreateRow) {
            return null;
          }
          const count = info.getValue();
          return count === '-' || count === 0 ? '-' : count;
        },
      }),
      columnHelper.accessor('venuesAssignedCount', {
        header: `Venues Assigned (${totalVenues} ${pluralize('venue', totalVenues)})`,
        size: 250,
        cell: ({ row, getValue }) => {
          if (row.original.isCreateRow) {
            return null;
          }
          const { rolledUpVenuesCount } = row.original;
          return (
            <>
              {getValue()}
              {rolledUpVenuesCount !== undefined && (
                <span className={styles['rolled-up-count']}> ({rolledUpVenuesCount} total)</span>
              )}
            </>
          );
        },
      }),
      columnHelper.display({
        id: 'action',
        size: 80,
        cell: ({ row }) => {
          if (row.original.isCreateRow) {
            return null;
          }

          const viewVenuesOption = {
            id: 'view-venues',
            name: 'View Venues',
            icon: ViewIcon,
            onOptionClick: () => handleViewVenuesClick(row.original.category_id),
          };

          if (row.original.isUncategorised) {
            return <EllipsisMenu options={[viewVenuesOption]} variant="ghost" hoverStyle="high-contrast" />;
          }

          return (
            <EllipsisMenu
              options={[
                viewVenuesOption,
                { id: 'divider-view', isDivider: true },
                ...(row.original.parent_category_id === null
                  ? [
                      {
                        id: 'duplicate-category',
                        name: 'Duplicate Category',
                        icon: DuplicateIcon,
                        isDisabled: areCrudActionsDisabled,
                        onOptionClick: () => openDuplicateTray(row.original),
                      },
                    ]
                  : []),
                {
                  id: 'create-sub-category',
                  name: 'Create Sub-category',
                  icon: AddPriceBandIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => {
                    handleCreateSubCategoryClick(row.original.category_id);
                    row.toggleExpanded(true);
                  },
                },
                {
                  id: 'edit-venues',
                  name: 'Edit Venues',
                  icon: EditIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleEditClick(row.original),
                },
                {
                  id: 'edit-name',
                  name: 'Edit name',
                  icon: TextIcon,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => setSelectedCategoryToEditName(row.original.category_id),
                },
                { id: 'divider-1', isDivider: true },
                {
                  id: 'move-category-up',
                  name: 'Move Up',
                  icon: ArrowUpIcon,
                  onOptionClick: () => reorderCategory(row.original.category_id, row.original.upDisplayOrder),
                  isDisabled: row.original.upDisplayOrder === undefined || areCrudActionsDisabled,
                  dataTestId: `move-up-category-${row.original.name}`,
                },
                {
                  id: 'move-category-down',
                  name: 'Move Down',
                  icon: ArrowDownIcon,
                  onOptionClick: () => reorderCategory(row.original.category_id, row.original.downDisplayOrder),
                  isDisabled: row.original.downDisplayOrder === undefined || areCrudActionsDisabled,
                  dataTestId: `move-down-category-${row.original.name}`,
                },
                {
                  id: 'move-to-category',
                  name: 'Move to Category',
                  icon: PriceBandIcon,
                  onOptionClick: () => handleMoveToCategoryClick(row.original),
                  isDisabled:
                    (row.original.parent_category_id === null &&
                      nestedCategories.filter((c) => c.category_id !== null).length <= MIN_ROOT_CATEGORIES) ||
                    areCrudActionsDisabled,
                },
                { id: 'divider-2', isDivider: true },
                {
                  id: 'delete-venues-category',
                  name: 'Delete',
                  hoverColour: 'tomato',
                  icon: Bin,
                  isDisabled: areCrudActionsDisabled,
                  onOptionClick: () => handleDeleteClick(row.original),
                },
              ]}
              variant="ghost"
              hoverStyle="high-contrast"
            />
          );
        },
      }),
    ],
    [totalVenues, isCreateCategoryOpen, data, nestedCategories.length, selectedCategoryToEditName],
  );

  if (isCategoriesLoading || isSyncInProgress) {
    return <SkeletonContainer hiddenTitle />;
  }

  return (
    <div className={styles['venue-categories-tab']}>
      <Table
        className={styles['table-fill-height']}
        data={tableData}
        columns={columns}
        getSubRows={(originalRow) =>
          originalRow.isUncategorised || originalRow.children.length === 0 || originalRow.isCreateRow
            ? undefined
            : originalRow.children
        }
      />

      {categoryToEdit && (
        <VenueAllocationModal
          venueCategories={categories}
          nestedVenueCategories={nestedCategories}
          isModalOpen={!!categoryToEdit}
          onModalClose={() => setCategoryToEdit(null)}
          entityId={categoryToEdit.category_id}
          entityName={categoryToEdit.name}
          venues={venues}
          selectedVenueIds={categoryToEdit.venue_ids}
          onSubmit={handleUpdateCategoryVenuesSubmit}
          isLoading={isUpdating}
        />
      )}

      {categoryToMove && (
        <MoveToCategoryPrompt
          open={!!categoryToMove}
          categoryName={categoryToMove.name}
          orderedCategories={orderedFlatCategories}
          category={categoryToMove}
          onClose={() => setCategoryToMove(null)}
          onSubmit={handleMoveToCategory}
          isSubmitting={isMoveLoading}
        />
      )}
      {categoryToDelete && (
        <Prompt
          promptHeading={`Are you sure you want to delete "${categoryToDelete.name}"?`}
          closePrompt={() => setCategoryToDelete(null)}
          open={!!categoryToDelete}
          size="small"
          backButton={true}
          backButtonText="Cancel"
          buttonText="Delete"
          buttonColour="tomato"
          buttonType="button"
          buttonHandler={handleDelete}
          isSubmitting={isDeleteLoading}
        >
          <Typography>Deleting this category will remove all associated venues and subcategories</Typography>
          <br />
          <Typography>This action cannot be undone</Typography>
        </Prompt>
      )}
    </div>
  );
};

export default VenueCategoriesTab;
